// The Pulsars nav entry's state: whether the feature is on in this region (the
// entry is hidden when it isn't) and how many things need the signed-in user
// (pending proposal batches, unanswered questions, halted pulsars). It starts from
// the switch GET /api/me already carried (`initiallyEnabled`), so the entry is in
// the menu on first paint, then is reseeded from GET /api/standing-agents/attention
// on mount, on agent-hub reconnects, on any standing nudge, and on the 30s
// fallback — the same live pattern as the pages.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { StandingAttention } from '@shared/api-types';
import { getAttention } from './standingAgentApi';
import { useStandingAgentLive } from '../pages/StandingAgents/useStandingAgentLive';

const DISABLED: StandingAttention = { enabled: false, count: 0, items: [] };

const seed = (enabled: boolean): StandingAttention => (enabled ? { enabled: true, count: 0, items: [] } : DISABLED);

/**
 * @param initiallyEnabled The switch GET /api/me carried for the signed-in user.
 * @param identity Who is signed in, and where (e.g. `${userId}:${tenantId}`). When it
 *   changes — a support sign-in or workspace switch replaces the user while the shell
 *   stays mounted — the state restarts from the new user's switch and is fetched again,
 *   so nothing carries over from the previous user.
 */
export function useStandingAttention(initiallyEnabled = false, identity = ''): StandingAttention {
  const [state, setState] = useState({ identity, initiallyEnabled, attention: seed(initiallyEnabled) });
  if (state.identity !== identity || state.initiallyEnabled !== initiallyEnabled) {
    // Reset during render (React's "adjust state when a prop changes"), not in an effect.
    setState({ identity, initiallyEnabled, attention: seed(initiallyEnabled) });
  }

  // The identity a fetch belongs to: an answer for a previous user is dropped.
  const identityRef = useRef(identity);
  useEffect(() => {
    identityRef.current = identity;
  }, [identity]);

  const load = useCallback(async () => {
    const forIdentity = identityRef.current;
    try {
      const next = await getAttention();
      if (identityRef.current !== forIdentity) return;
      // A missing/odd body (an older API, a test double) reads as "off".
      const attention = next && typeof next.enabled === 'boolean' ? next : DISABLED;
      setState((s) => (s.identity === forIdentity ? { ...s, attention } : s));
    } catch {
      // A transient failure: keep what the nav shows now (never an error in the
      // nav, and the entry doesn't flicker away). The feature being off is an
      // answer (enabled: false), not a failure.
    }
  }, []);
  const reseed = useStandingAgentLive(load);

  // A different user: fetch theirs (mount is already covered by the live hook).
  const fetchedFor = useRef(identity);
  useEffect(() => {
    if (fetchedFor.current === identity) return;
    fetchedFor.current = identity;
    void reseed();
  }, [identity, reseed]);

  return state.attention;
}
